import { useState, useCallback } from 'react';
import {
  DndContext,
  DragOverlay,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Card, Button } from '../ui';
import Input, { InputLabel, InputGroup } from '../ui/Input';
import { Textarea } from '../ui/Input';
import { cn } from '../../lib/cn';

const QUESTION_TYPES = [
  { id: 'multiple_choice', label: 'Multiple Choice', maxCount: 10, defaultCount: 3 },
  { id: 'short_answer', label: 'Short Answer', maxCount: 10, defaultCount: 1 },
  { id: 'essay', label: 'Essay', maxCount: 5, defaultCount: 1 },
  { id: 'table_completion', label: 'Categorization', maxCount: 5, defaultCount: 1 },
];

const BLOOM_LEVELS = [
  { id: 'remember', label: 'Remember', description: 'Recall facts and basic concepts' },
  { id: 'understand', label: 'Understand', description: 'Explain ideas or concepts' },
  { id: 'apply', label: 'Apply', description: 'Use information in new situations' },
  { id: 'analyze', label: 'Analyze', description: 'Draw connections among ideas' },
  { id: 'evaluate', label: 'Evaluate', description: 'Justify a decision or course of action' },
  { id: 'create', label: 'Create', description: 'Produce new or original work' },
];

function SortableCluster({ cluster, index, onUpdate, onRemove, isOverlay = false }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: cluster.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const typeConfig = QUESTION_TYPES.find(t => t.id === cluster.type);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'group',
        isDragging && 'opacity-50',
        isOverlay && 'opacity-100 shadow-2xl'
      )}
    >
      <Card className={cn(
        'p-4 border-2 transition-all',
        isDragging ? 'border-gray-400 dark:border-gray-500' : 'border-gray-200 dark:border-gray-700',
        'hover:border-gray-300 dark:hover:border-gray-600'
      )}>
        <div className="flex items-start gap-4">
          {/* Drag Handle */}
          <button
            {...attributes}
            {...listeners}
            className="mt-1 p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 cursor-grab active:cursor-grabbing touch-none"
            aria-label="Drag to reorder"
          >
            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
            </svg>
          </button>

          {/* Cluster Content */}
          <div className="flex-1 space-y-4">
            {/* Top Row: Type, Count, Bloom */}
            <div className="flex flex-wrap items-end gap-4">
              {/* Question Type */}
              <div className="flex-1 min-w-[140px]">
                <InputLabel className="text-xs mb-1">Type</InputLabel>
                <select
                  value={cluster.type}
                  onChange={(e) => onUpdate(cluster.id, { type: e.target.value })}
                  className={cn(
                    'w-full px-3 py-2 rounded-lg border text-sm',
                    'bg-white dark:bg-gray-800',
                    'border-gray-200 dark:border-gray-600',
                    'text-gray-900 dark:text-white',
                    'focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-white'
                  )}
                >
                  {QUESTION_TYPES.map(type => (
                    <option key={type.id} value={type.id}>{type.label}</option>
                  ))}
                </select>
              </div>

              {/* Count */}
              <div className="w-20">
                <InputLabel className="text-xs mb-1">Count</InputLabel>
                <Input
                  type="number"
                  min="1"
                  max={typeConfig?.maxCount || 10}
                  value={cluster.count}
                  onChange={(e) => onUpdate(cluster.id, { count: Math.max(1, parseInt(e.target.value) || 1) })}
                  className="text-center"
                />
              </div>

              {/* Bloom Level */}
              <div className="flex-1 min-w-[160px]">
                <InputLabel className="text-xs mb-1">Skill Level</InputLabel>
                <select
                  value={cluster.bloomLevel}
                  onChange={(e) => onUpdate(cluster.id, { bloomLevel: e.target.value })}
                  className={cn(
                    'w-full px-3 py-2 rounded-lg border text-sm',
                    'bg-white dark:bg-gray-800',
                    'border-gray-200 dark:border-gray-600',
                    'text-gray-900 dark:text-white',
                    'focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-white'
                  )}
                >
                  {BLOOM_LEVELS.map(level => (
                    <option key={level.id} value={level.id}>{level.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Prompt/Direction Field */}
            <div>
              <InputLabel className="text-xs mb-1">
                AI Direction <span className="text-gray-400 font-normal">(optional)</span>
              </InputLabel>
              <Input
                value={cluster.prompt || ''}
                onChange={(e) => onUpdate(cluster.id, { prompt: e.target.value })}
                placeholder={`e.g., "Focus on vocabulary" or "Ask about the main argument"`}
                className="text-sm"
              />
            </div>
          </div>

          {/* Remove Button */}
          <button
            onClick={() => onRemove(cluster.id)}
            className="mt-1 p-1.5 rounded text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
            aria-label="Remove cluster"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Cluster Summary */}
        <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700 flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
          <span className="font-medium text-gray-700 dark:text-gray-300">
            {cluster.count} {typeConfig?.label || cluster.type}
          </span>
          <span className="text-gray-300 dark:text-gray-600">|</span>
          <span className={cn(
            'px-1.5 py-0.5 rounded',
            'bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400'
          )}>
            {BLOOM_LEVELS.find(l => l.id === cluster.bloomLevel)?.label || cluster.bloomLevel}
          </span>
          {cluster.prompt && (
            <>
              <span className="text-gray-300 dark:text-gray-600">|</span>
              <span className="truncate max-w-[200px] italic">"{cluster.prompt}"</span>
            </>
          )}
        </div>
      </Card>
    </div>
  );
}

export default function QuestionClusterBuilder({ clusters, onChange, disabled = false }) {
  const [activeId, setActiveId] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragStart = useCallback((event) => {
    setActiveId(event.active.id);
  }, []);

  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;
    setActiveId(null);

    if (over && active.id !== over.id) {
      const oldIndex = clusters.findIndex(c => c.id === active.id);
      const newIndex = clusters.findIndex(c => c.id === over.id);
      onChange(arrayMove(clusters, oldIndex, newIndex));
    }
  }, [clusters, onChange]);

  const handleAddCluster = useCallback(() => {
    const defaultType = QUESTION_TYPES[0];
    const newCluster = {
      id: `cluster-${Date.now()}`,
      type: defaultType.id,
      count: defaultType.defaultCount,
      bloomLevel: 'understand',
      prompt: '',
    };
    onChange([...clusters, newCluster]);
  }, [clusters, onChange]);

  const handleUpdateCluster = useCallback((id, updates) => {
    // If type is changing, also update count to the new type's default
    if (updates.type) {
      const newTypeConfig = QUESTION_TYPES.find(t => t.id === updates.type);
      if (newTypeConfig) {
        updates.count = newTypeConfig.defaultCount;
      }
    }
    onChange(clusters.map(c => c.id === id ? { ...c, ...updates } : c));
  }, [clusters, onChange]);

  const handleRemoveCluster = useCallback((id) => {
    onChange(clusters.filter(c => c.id !== id));
  }, [clusters, onChange]);

  const activeCluster = activeId ? clusters.find(c => c.id === activeId) : null;
  const totalQuestions = clusters.reduce((sum, c) => sum + c.count, 0);

  return (
    <div className="space-y-4">
      {/* Cluster List */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={clusters.map(c => c.id)}
          strategy={verticalListSortingStrategy}
        >
          <div className="space-y-3">
            {clusters.map((cluster, index) => (
              <SortableCluster
                key={cluster.id}
                cluster={cluster}
                index={index}
                onUpdate={handleUpdateCluster}
                onRemove={handleRemoveCluster}
              />
            ))}
          </div>
        </SortableContext>

        <DragOverlay>
          {activeCluster ? (
            <SortableCluster
              cluster={activeCluster}
              index={0}
              onUpdate={() => {}}
              onRemove={() => {}}
              isOverlay
            />
          ) : null}
        </DragOverlay>
      </DndContext>

      {/* Add Cluster Button */}
      <button
        onClick={handleAddCluster}
        disabled={disabled}
        className={cn(
          'w-full p-4 rounded-lg border-2 border-dashed',
          'flex items-center justify-center gap-2',
          'text-sm font-medium transition-all',
          disabled
            ? 'border-gray-200 text-gray-400 cursor-not-allowed dark:border-gray-700 dark:text-gray-600'
            : 'border-gray-300 text-gray-600 hover:border-gray-400 hover:text-gray-900 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-400 dark:hover:border-gray-500 dark:hover:text-white dark:hover:bg-gray-800'
        )}
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
        Add Question Group
      </button>

      {/* Summary */}
      {clusters.length > 0 && (
        <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400 pt-2">
          <span>
            {clusters.length} group{clusters.length !== 1 ? 's' : ''} · {totalQuestions} total question{totalQuestions !== 1 ? 's' : ''}
          </span>
          <span className="text-xs">
            Drag groups to reorder
          </span>
        </div>
      )}
    </div>
  );
}
